import * as React from "react";
import { TURNSTILE_SITE_KEY, loadTurnstile, turnstileEnabled } from "@/lib/turnstile";
import { useTheme } from "@/lib/theme";

/**
 * The challenge box itself. `lib/turnstile.ts` has the argument for why this
 * exists and how the two switches interact; this file is only the DOM.
 *
 * ── It fails open ──────────────────────────────────────────────────────────
 *
 * An ad blocker, a corporate proxy and a Cloudflare outage all present
 * identically: the script never loads. Rendering a dead box and disabling the
 * submit would lock a paying customer out of their own console over a third
 * party we do not control, so the form stays submittable with no token. If the
 * server half is on, GoTrue refuses it and `describeAuthError` says what to try.
 * A refused submit with a sentence beats a permanently greyed button with none.
 *
 * ── The token is not rendered anywhere ─────────────────────────────────────
 *
 * It goes to `onToken` and into a `captchaToken` argument. There is no hidden
 * input, because nothing here is a native form post - every call site is a
 * `preventDefault`ed submit handler calling `lib/auth.tsx`.
 */
export function TurnstileWidget({
  onToken,
  onUnavailable,
  /** Bump to re-mint. See `TurnstileState.reset` for when, and why it matters. */
  resetKey = 0,
  appearance = "always",
}: {
  onToken: (token: string) => void;
  onUnavailable?: () => void;
  resetKey?: number;
  appearance?: "always" | "interaction-only";
}) {
  const { theme } = useTheme();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const widgetIdRef = React.useRef<string | null>(null);
  const [blocked, setBlocked] = React.useState(false);

  // Through refs, so a call site passing an inline arrow does not tear the
  // widget down and re-mint a token every time its parent re-renders - which on
  // a sign-in form is once per keystroke.
  const onTokenRef = React.useRef(onToken);
  const onUnavailableRef = React.useRef(onUnavailable);
  React.useEffect(() => {
    onTokenRef.current = onToken;
    onUnavailableRef.current = onUnavailable;
  }, [onToken, onUnavailable]);

  React.useEffect(() => {
    if (!turnstileEnabled) return;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        setBlocked(false);
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme,
          size: "flexible",
          appearance,
          callback: (token: string) => onTokenRef.current(token),
          // Both clear the token rather than reporting anything. An expired
          // challenge is not a failure the reader has to be told about - it is
          // one the next submit re-mints, and an error on a form nobody has
          // pressed yet is alarming for no reason.
          "expired-callback": () => onTokenRef.current(""),
          "error-callback": () => onTokenRef.current(""),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setBlocked(true);
        onUnavailableRef.current?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // `theme` is a dependency on purpose: Turnstile bakes its palette in at
    // render and offers no way to restyle afterwards, so a light widget left on
    // a dark auth panel is a white card nothing can fix. Re-minting on a theme
    // switch is cheap and rare.
  }, [resetKey, theme, appearance]);

  if (!turnstileEnabled) return null;

  if (blocked) {
    return (
      <p className="auth-note" role="status">
        The verification challenge could not be loaded. You can still try to continue - if it is
        refused, turn off any ad blocker for this site and reload.
      </p>
    );
  }

  return (
    <div
      ref={containerRef}
      // "interaction-only" occupies no space until Cloudflare actually shows
      // a challenge, so it does not need the reserved-height floor below.
      className={
        appearance === "interaction-only" ? "auth-captcha auth-captcha--quiet" : "auth-captcha"
      }
    />
  );
}
